// Lightweight wrapper to initialize Firebase Remote Config in the browser and expose helpers.
// Reads NEXT_PUBLIC_FIREBASE_* env vars that Next.js exposes to the client.


export type RemoteConfigClient = {
  fetchAndActivate: () => Promise<boolean>;
  getNumber: (key: string, fallback: number) => number;
  getString: (key: string, fallback: string) => string;
  getAllConfigValues: () => {
    welcomeBonus: number;
    unlockCost: number;
    notificationReward: number;
    emailReward: number;
    shareYoutubeReward: number;
    shareFacebookReward: number;
    shareInstagramReward: number;
    friendShareReward: number;
    watch10Reward: number;
    watch20Reward: number;
    baseCheckinAmount: number;
  };
};

export async function initRemoteConfig(): Promise<RemoteConfigClient | null> {
  try {
    // Only run in browser
    if (typeof window === 'undefined') return null;
    // Use static config from source (preferred) and fall back to any globals if present.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let staticCfg: any = {};
    try {
      const cfgMod = await import('@/app/lib/firebaseConfig');
      // Prefer the new FIREBASE_STATIC_CONFIG export, fall back to legacy firebaseConfig
      staticCfg = cfgMod?.FIREBASE_STATIC_CONFIG || cfgMod?.firebaseConfig || {};
    } catch {
      staticCfg = {};
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const envCfg = (window as any).__NEXT_PUBLIC_FIREBASE_API_KEY ? {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      apiKey: (window as any).__NEXT_PUBLIC_FIREBASE_API_KEY,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      authDomain: (window as any).__NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      projectId: (window as any).__NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      appId: (window as any).__NEXT_PUBLIC_FIREBASE_APP_ID,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      measurementId: (window as any).__NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
    } : {};

    const cfg = {
      apiKey: staticCfg.apiKey || envCfg.apiKey,
      authDomain: staticCfg.authDomain || envCfg.authDomain,
      projectId: staticCfg.projectId || envCfg.projectId,
      appId: staticCfg.appId || envCfg.appId,
      measurementId: staticCfg.measurementId || envCfg.measurementId,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    if (!cfg.apiKey || !cfg.projectId) {
      // no firebase configured
      return null;
    }

  const firebaseAppMod = await import('firebase/app');
  const remoteConfigMod = await import('firebase/remote-config');
  const { initializeApp, getApps, getApp } = firebaseAppMod;
  const { getRemoteConfig, fetchAndActivate, getValue } = remoteConfigMod;

    let app;
    if (!getApps || getApps().length === 0) {
      // Prefer the full static config if available so Messaging (and other services)
      // have all required fields (messagingSenderId, vapidKey, etc.). Fall back to
      // the minimal cfg built from env/static fields.
      const initOpts = (staticCfg && staticCfg.apiKey && staticCfg.projectId) ? staticCfg : cfg;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      app = initializeApp(initOpts as any);
    } else {
      app = getApp();
    }

    const rc = getRemoteConfig(app);
    // Set cache duration to 12 hours to reduce Firebase fetch requests
    try {
      // 12 hours = 43200000 milliseconds
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rc.settings as any).minimumFetchIntervalMillis = 43200000; // 12 hours
    } catch { /* noop */ }

    // initial fetch & activate
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await fetchAndActivate(rc as any);
    } catch {
      // ignore fetch failures - we'll still allow reading default values
      console.warn('[remote-config] initial fetch failed');
    }

    const client: RemoteConfigClient = {
      fetchAndActivate: async () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const activated = await fetchAndActivate(rc as any);
          return Boolean(activated);
        } catch {
          console.warn('[remote-config] fetchAndActivate failed');
          return false;
        }
      },
      getNumber: (key: string, fallback: number) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const val = getValue(rc as any, key);
          const s = val ? val.asString() : '';
          const n = Number(s);
          if (!isNaN(n)) return n;
          return fallback;
        } catch {
          return fallback;
        }
      },
      getString: (key: string, fallback: string) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const val = getValue(rc as any, key);
          return val ? val.asString() : fallback;
        } catch {
          return fallback;
        }
      },
      getAllConfigValues: () => {
        try {
          // Fetch the JSON string from "Dramaflicks_Web" key
          const jsonString = client.getString('Dramaflicks_Web', '{}');
          const parsed = JSON.parse(jsonString);
          
          // Return parsed values with fallbacks
          return {
            welcomeBonus: Number(parsed.welcomeBonus) || 5,
            unlockCost: Number(parsed.unlockCost) || 2,
            notificationReward: Number(parsed.notificationReward) || 5,
            emailReward: Number(parsed.emailReward) || 5,
            shareYoutubeReward: Number(parsed.shareYoutubeReward) || 5,
            shareFacebookReward: Number(parsed.shareFacebookReward) || 5,
            shareInstagramReward: Number(parsed.shareInstagramReward) || 5,
            friendShareReward: Number(parsed.friendShareReward) || 5,
            watch10Reward: Number(parsed.watch10Reward) || 5,
            watch20Reward: Number(parsed.watch20Reward) || 10,
            baseCheckinAmount: Number(parsed.baseCheckinAmount) || 5,
          };
        } catch (error) {
          console.warn('[remote-config] Failed to parse Dramaflicks_Web:', error);
          // Return default values if parsing fails
          return {
            welcomeBonus: 5,
            unlockCost: 2,
            notificationReward: 5,
            emailReward: 5,
            shareYoutubeReward: 5,
            shareFacebookReward: 5,
            shareInstagramReward: 5,
            friendShareReward: 5,
            watch10Reward: 5,
            watch20Reward: 10,
            baseCheckinAmount: 5,
          };
        }
      },
    };

    return client;
  } catch {
    console.warn('[remote-config] init failed');
    return null;
  }
}
